import { buildTenantDetailSupportReturnPath } from "../tenantDeepLink.utils";

const buildEnterTenantAction = (permissions, path, label = "Open in tenant") => {
  if (!permissions.enterTenant) {
    return permissions.ghost ? { id: "ghost", label: "Ghost as user" } : null;
  }

  return { id: "enter_tenant_path", label, path };
};

const buildGhostSecondaryAction = (permissions) =>
  permissions.ghost ? { id: "ghost", label: "Ghost as user" } : null;

export const getTenantOperationalPlaybooks = (_tenant, permissions = {}) => {
  if (!permissions.enterTenant && !permissions.ghost) {
    return [];
  }

  return [
    {
      id: "cos_funding_case_notes",
      category: "operational",
      ticketRefs: [7],
      title: "Set up funding & case notes",
      description: "Configure COS participant funding and case note workflows.",
      selfServiceSteps: [
        "SP Admin opens COS → Participants and selects the participant.",
        "Add funding under the participant funding tab, then create case notes from the profile.",
      ],
      steps: [
        "Confirm the tenant has COS enabled and the participant exists.",
        "Open the participant list and navigate to the participant profile.",
        "Check funding is active and case note templates are available.",
        "If the user is stuck, ghost as the SP Admin to walk through the same screens.",
      ],
      primaryAction: buildEnterTenantAction(
        permissions,
        "/cos/participant-list",
        "Open COS participants"
      ),
      secondaryAction: buildGhostSecondaryAction(permissions),
    },
    {
      id: "split_shift_line_items",
      category: "operational",
      ticketRefs: [8],
      title: "Edit split shift line items",
      description: "Adjust roster line items for split or shared shifts.",
      selfServiceSteps: [
        "Open Rostering → Schedule and locate the shift.",
        "Open the shift detail view and edit line items from the allocation section.",
      ],
      steps: [
        "Confirm the shift is not locked by an approved timesheet or invoice.",
        "Open the roster schedule and locate the shift by date/worker.",
        "Edit line items in the shift detail drawer.",
        "If blocked, check timesheet approval and invoicing status first.",
      ],
      primaryAction: buildEnterTenantAction(permissions, "/rostering/schedule"),
      secondaryAction: buildGhostSecondaryAction(permissions),
    },
    {
      id: "shift_end_questions",
      category: "operational",
      ticketRefs: [9],
      title: "Configure shift end questions",
      description: "Enable and manage questions workers answer when ending a shift.",
      selfServiceSteps: [
        "SP Admin opens Settings → Rostering settings.",
        "Enable Shift End Questions and add the required questions.",
      ],
      steps: [
        "Open rostering settings and confirm Shift End Questions is enabled.",
        "Add or edit questions and save the rostering settings.",
        "Ask the worker to end a test shift to verify prompts appear.",
      ],
      primaryAction: buildEnterTenantAction(
        permissions,
        "/settings/rostering-setting",
        "Open rostering settings"
      ),
      secondaryAction: buildGhostSecondaryAction(permissions),
    },
    {
      id: "roles_permissions",
      category: "operational",
      ticketRefs: [10],
      title: "Roles & permissions",
      description: "Fix access issues caused by missing roles, regions, or permissions.",
      selfServiceSteps: [
        "SP Admin opens Settings → Roles & permissions.",
        "Assign the correct role and confirm region access for managers.",
      ],
      steps: [
        "Identify the user's role and assigned regions.",
        "Open roles & permissions and verify the role includes the required permission.",
        "If permissions were recently changed, ask the user to sign out and back in.",
        "Ghost as the affected user to confirm the menu items they can see.",
      ],
      primaryAction: buildEnterTenantAction(
        permissions,
        "/roles-permissions-setting",
        "Open roles & permissions"
      ),
      secondaryAction: buildGhostSecondaryAction(permissions),
    },
    {
      id: "worker_accept_shift",
      category: "operational",
      ticketRefs: [11],
      title: "Worker can't accept published shift",
      description: "Troubleshoot why a worker cannot accept a published roster shift.",
      selfServiceSteps: [
        "Confirm the worker is active and has completed mandatory compliance documents.",
        "Republish the shift if it was changed after the worker received it.",
      ],
      steps: [
        "Verify the worker profile is active and linked to the correct tenant.",
        "Check mandatory compliance documents are complete.",
        "Confirm the shift is published and assigned to the worker.",
        "Ghost as the worker to reproduce the accept action in the worker portal/app.",
      ],
      primaryAction: buildEnterTenantAction(permissions, "/rostering/schedule"),
      secondaryAction: buildGhostSecondaryAction(permissions),
    },
    {
      id: "staff_see_roster",
      category: "operational",
      ticketRefs: [12],
      title: "Staff can't see roster",
      description: "Troubleshoot missing roster visibility for managers or workers.",
      selfServiceSteps: [
        "Confirm the user is signing in with the correct role (manager vs worker).",
        "Managers need roster permissions and region access for the relevant sites.",
      ],
      steps: [
        "Check user type and assigned role permissions.",
        "For managers, confirm region scope includes the roster location.",
        "Open the roster schedule as SP Admin to verify shifts exist for the date range.",
        "Ghost as the user to compare what they see.",
      ],
      primaryAction: buildEnterTenantAction(permissions, "/rostering/schedule"),
      secondaryAction: buildGhostSecondaryAction(permissions),
    },
    {
      id: "invoice_billable_hours",
      category: "operational",
      ticketRefs: [13],
      title: "Generate invoice from billable hours",
      description: "Create roster or COS invoices from approved billable time.",
      selfServiceSteps: [
        "Approve relevant timesheets first.",
        "Open Rostering → Invoicing or COS → Invoice to generate from billable hours.",
      ],
      steps: [
        "Confirm timesheets are approved for the billing period.",
        "For roster billing, open roster invoicing and start a new invoice run.",
        "For COS, open COS invoicing if the claim is COS-based.",
        "Review line items before sending or exporting the invoice.",
      ],
      primaryAction: buildEnterTenantAction(
        permissions,
        "/rostering/invoicing",
        "Open roster invoicing"
      ),
      secondaryAction: buildGhostSecondaryAction(permissions),
    },
    {
      id: "reports_shift_case_notes",
      category: "operational",
      ticketRefs: [14],
      title: "Shift notes & case notes reports",
      description: "Run reporting for shift notes and case notes.",
      selfServiceSteps: [
        "Open Reporting and choose Case Notes or Timesheet reports.",
        "Set the date range and export or view the report.",
      ],
      steps: [
        "Open the relevant reporting screen for case notes or timesheets.",
        "Set filters (date range, participant/worker, region).",
        "Export or print from the report view if required.",
      ],
      primaryAction: buildEnterTenantAction(
        permissions,
        "/reporting/case-notes-reports",
        "Open case notes reports"
      ),
      secondaryAction: buildGhostSecondaryAction(permissions),
    },
    {
      id: "upgrade_subscription",
      category: "operational",
      ticketRefs: [15],
      title: "Upgrade subscription",
      description: "Help the tenant change or upgrade their subscription plan.",
      selfServiceSteps: [
        "SP Admin opens Subscription → Plans and selects the new plan.",
        "Complete Stripe checkout if the tenant is not on an enterprise wildcard account.",
      ],
      steps: [
        "Check whether the tenant is on a wildcard / enterprise billing arrangement.",
        "If self-serve billing applies, open subscription plans in tenant context.",
        "If wildcard, use the Billing tab and Stripe dashboard instead of tenant checkout.",
      ],
      primaryAction: buildEnterTenantAction(
        permissions,
        "/subscription/plans",
        "Open subscription plans"
      ),
      secondaryAction: buildGhostSecondaryAction(permissions),
    },
    {
      id: "worker_availability",
      category: "operational",
      ticketRefs: [16],
      title: "Availability & unavailability",
      description: "Manage worker availability, leave, or unavailability requests.",
      selfServiceSteps: [
        "Workers update availability from their worker portal where enabled.",
        "Managers review leave/unavailability from rostering leave requests.",
      ],
      steps: [
        "Confirm whether the request is worker availability or manager-approved leave.",
        "Open leave requests for manager review workflows.",
        "Ghost as the worker if the issue is on the worker-facing screen.",
      ],
      primaryAction: buildEnterTenantAction(
        permissions,
        "/rostering/leaverequests",
        "Open leave requests"
      ),
      secondaryAction: buildGhostSecondaryAction(permissions),
    },
    {
      id: "delete_past_shift",
      category: "operational",
      ticketRefs: [18],
      title: "Delete or adjust a past shift",
      description: "Remove or change a shift that already occurred.",
      selfServiceSteps: [
        "Unapprove the linked timesheet before deleting the shift.",
        "Void any invoice that includes the shift if invoicing has run.",
      ],
      steps: [
        "Open the roster schedule and locate the shift.",
        "If timesheet is approved, unapprove it first (void invoice if required).",
        "Delete or edit the shift once blocking billing states are cleared.",
      ],
      primaryAction: buildEnterTenantAction(permissions, "/rostering/schedule"),
      secondaryAction: buildGhostSecondaryAction(permissions),
    },
  ];
};

export const buildTenantSupportReturnPath = buildTenantDetailSupportReturnPath;
